import React from 'react';
import { CheckCircle2, UserPlus, X } from 'lucide-react';
import type { TranscriptExtraction } from '../../types/models';
import { transcriptsCopy } from '../../copy/transcripts';
import ConfidenceBadge from './ConfidenceBadge';
import WhyAffordance from '../ai/WhyAffordance';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// ExtractionCard — one row in the extractions tab. Confidence-first, with
// verb-first primary CTAs (Accept into plan / Assign / Dismiss) next to the
// title so the user can act without scanning to the far edge of the screen.
// ---------------------------------------------------------------------------

const TYPE_TONE: Record<string, string> = {
  Task: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  Decision: 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
  Briefing: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  Review: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  FollowUp: 'bg-purple-500/15 text-purple-700 dark:text-purple-300',
  Draft: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300',
  Reading: 'bg-lime-500/15 text-lime-700 dark:text-lime-400',
};

const PRIORITY_TONE: Record<string, string> = {
  Critical: 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
  High: 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
  Medium: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  Low: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
};

interface ExtractionCardProps {
  extraction: TranscriptExtraction;
  selected?: boolean;
  onToggleSelect?: () => void;
  onAccept?: () => void;
  onAssign?: () => void;
  onDismiss?: () => void;
  disabled?: boolean;
}

function SoftChip({ label, tone }: { label: string; tone: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
        tone,
      )}
    >
      {label}
    </span>
  );
}

export function ExtractionCard({
  extraction,
  selected = false,
  onToggleSelect,
  onAccept,
  onAssign,
  onDismiss,
  disabled = false,
}: ExtractionCardProps) {
  const isHandled = extraction.wasAccepted || extraction.wasDismissed;
  const stateLabel = extraction.wasAccepted
    ? transcriptsCopy.statusAccepted
    : extraction.wasDismissed
      ? transcriptsCopy.statusDismissed
      : null;

  return (
    <div
      className={cn(
        'rounded-2xl border bg-card p-3 transition-colors',
        selected ? 'border-primary bg-primary/[0.04]' : 'border-border',
        isHandled && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-2">
        {!isHandled && onToggleSelect && (
          <Checkbox
            checked={selected}
            onCheckedChange={onToggleSelect}
            className="mt-0.5"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold">{extraction.title}</p>
                <WhyAffordance
                  rationale={extraction.rationale}
                  size="compact"
                  label="Why this extraction"
                />
                <ConfidenceBadge score={extraction.confidenceScore} />
                {stateLabel && (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
                      extraction.wasAccepted
                        ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                        : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {stateLabel}
                  </span>
                )}
              </div>
              {extraction.description && (
                <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                  {extraction.description}
                </p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <SoftChip
                  label={extraction.workItemType}
                  tone={TYPE_TONE[extraction.workItemType] || 'bg-muted text-muted-foreground'}
                />
                <SoftChip
                  label={extraction.priority}
                  tone={PRIORITY_TONE[extraction.priority] || 'bg-muted text-muted-foreground'}
                />
                <p className="text-xs text-muted-foreground">
                  {extraction.suggestedDurationMinutes} min
                </p>
                {extraction.assignedTo && (
                  <p className="text-xs text-muted-foreground">for {extraction.assignedTo}</p>
                )}
                {extraction.dueContext && (
                  <p className="text-xs text-muted-foreground">{extraction.dueContext}</p>
                )}
              </div>
            </div>
            {!isHandled && (
              <div className="flex shrink-0 gap-1">
                {onAccept && (
                  <Button
                    size="sm"
                    onClick={onAccept}
                    disabled={disabled}
                    className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {transcriptsCopy.actionAccept}
                  </Button>
                )}
                {onAssign && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onAssign}
                    disabled={disabled}
                    className="gap-1.5"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    {transcriptsCopy.actionAssign}
                  </Button>
                )}
                {onDismiss && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onDismiss}
                    disabled={disabled}
                    className="gap-1.5 text-muted-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                    {transcriptsCopy.actionDismiss}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExtractionCard;
